import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatDate, formatRelativeTime } from "@/lib/utils";
import { toast } from "sonner";
import AuthModal from "@/components/AuthModal";
import axios from "axios";
import { 
  Activity, 
  Users, 
  MessageSquare,
  BookMarked,
  TrendingUp,
  RefreshCw,
  Lock,
  Smile,
  Meh,
  Frown,
  Heart,
  Mail
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOOD_ICONS = {
  1: { icon: Frown, color: "text-red-500" },
  2: { icon: Frown, color: "text-orange-500" },
  3: { icon: Meh, color: "text-yellow-500" },
  4: { icon: Smile, color: "text-lime-500" },
  5: { icon: Smile, color: "text-green-500" }
};

export default function ProviderPortal() {
  const { user, loading: authLoading } = useAuth();
  const [insights, setInsights] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientJournal, setPatientJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (user?.role === "provider" || user?.role === "admin" || user?.role === "administrator") {
        loadData();
      } else {
        setLoading(false);
      }
    }
  }, [user, authLoading]);

  const getToken = () => localStorage.getItem("hospice_token");

  const loadData = async () => {
    try {
      const [insightsRes, patientsRes] = await Promise.all([
        axios.get(`${API_URL}/api/physician/insights`, { headers: { Authorization: `Bearer ${getToken()}` } }),
        axios.get(`${API_URL}/api/physician/patients`, { headers: { Authorization: `Bearer ${getToken()}` } })
      ]);
      setInsights(insightsRes.data);
      setPatients(patientsRes.data.patients || patientsRes.data);
    } catch (error) {
      console.error("Error loading data:", error);
      // Fallback to demo data
      setInsights({
        pending_orders: 5,
        pending_cti: 3,
        active_patients: 24,
        recent_changes: 8
      });
      setPatients([]);
    } finally {
      setLoading(false);
    }
  };

  const loadPatientJournal = async (patientId) => {
    try {
      const response = await axios.get(`${API_URL}/api/physician/patients/${patientId}/journal`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setPatientJournal(response.data.entries || response.data);
    } catch (error) {
      toast.error("Failed to load patient journal");
      setPatientJournal([]);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    loadPatientJournal(patient.id);
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="provider-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || (user.role !== "provider" && user.role !== "admin" && user.role !== "administrator")) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center p-6" data-testid="provider-restricted">
        <Card className="max-w-md w-full text-center glass-card border-0 shadow-soft">
          <CardContent className="p-8">
            <Lock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-serif text-2xl mb-2">Provider Access Required</h2>
            <p className="text-muted-foreground mb-6">
              This portal is for healthcare providers. Please sign in with a provider account.
            </p>
            {!user && <Button onClick={() => setShowAuthModal(true)}>Sign In</Button>}
          </CardContent>
        </Card>
        <AuthModal open={showAuthModal} onOpenChange={setShowAuthModal} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30" data-testid="provider-portal-page">
      {/* Header */}
      <section className="bg-white border-b border-border/50 py-6">
        <div className="container-max px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="heading-2 text-foreground mb-1">Provider Portal</h1>
              <p className="text-muted-foreground">Patient insights, journal access, and care management</p>
            </div>
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="rounded-full">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-6 px-6 md:px-12">
        <div className="container-max">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="glass-card border-0 shadow-soft">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Total Patients</p>
                    <p className="text-2xl font-serif font-semibold text-foreground">{insights?.total_patients || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-primary/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-0 shadow-soft">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Active This Week</p>
                    <p className="text-2xl font-serif font-semibold text-foreground">{insights?.active_this_week || 0}</p>
                  </div>
                  <Activity className="w-8 h-8 text-teal-500/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-0 shadow-soft">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Unread Messages</p>
                    <p className="text-2xl font-serif font-semibold text-foreground">{insights?.unread_messages || 0}</p>
                  </div>
                  <Mail className="w-8 h-8 text-rose-500/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-0 shadow-soft">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Chat Sessions</p>
                    <p className="text-2xl font-serif font-semibold text-foreground">{insights?.chat_interactions_this_week || 0}</p>
                  </div>
                  <MessageSquare className="w-8 h-8 text-blue-500/30" />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card border-0 shadow-soft col-span-2 lg:col-span-1">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground mb-2">Top Symptom This Week</p>
                {insights?.symptom_trends?.[0] ? (
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{insights.symptom_trends[0].symptom}</Badge>
                    <span className="text-sm text-muted-foreground">{insights.symptom_trends[0].count}x</span>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-12 px-6 md:px-12">
        <div className="container-max">
          <Tabs defaultValue="patients" className="space-y-6">
            <TabsList className="bg-white shadow-soft">
              <TabsTrigger value="patients"><Users className="w-4 h-4 mr-2" />Patients</TabsTrigger>
              <TabsTrigger value="insights"><TrendingUp className="w-4 h-4 mr-2" />Insights</TabsTrigger>
            </TabsList>

            <TabsContent value="patients">
              <div className="grid lg:grid-cols-3 gap-6">
                {/* Patient List */}
                <Card className="glass-card border-0 shadow-soft lg:col-span-1">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-serif text-lg">Patient List</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <ScrollArea className="h-[500px]">
                      {patients.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No patients registered</p>
                      ) : (
                        <div className="space-y-1 p-2">
                          {patients.map((patient) => {
                            const MoodIcon = patient.last_mood ? MOOD_ICONS[patient.last_mood]?.icon : Meh;
                            return (
                              <button
                                key={patient.id}
                                onClick={() => handlePatientSelect(patient)}
                                className={`w-full text-left p-3 rounded-xl transition-all ${
                                  selectedPatient?.id === patient.id
                                    ? "bg-primary/10 text-primary"
                                    : "hover:bg-muted"
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-medium text-sm">{patient.name}</p>
                                    <p className="text-xs text-muted-foreground">{patient.email}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {patient.last_mood && (
                                      <MoodIcon className={`w-4 h-4 ${MOOD_ICONS[patient.last_mood]?.color}`} />
                                    )}
                                    {patient.unread_messages > 0 && (
                                      <Badge variant="destructive" className="text-xs">{patient.unread_messages}</Badge>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Patient Journal */}
                <Card className="glass-card border-0 shadow-soft lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                      <BookMarked className="w-5 h-5 text-primary" />
                      {selectedPatient ? `${selectedPatient.name}'s Journal` : "Select a Patient"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {selectedPatient ? (
                      patientJournal.length === 0 ? (
                        <p className="text-center text-muted-foreground py-8">No journal entries yet</p>
                      ) : (
                        <ScrollArea className="h-[400px]">
                          <div className="space-y-4">
                            {patientJournal.map((entry) => {
                              const MoodIcon = entry.mood ? MOOD_ICONS[entry.mood]?.icon : Meh;
                              return (
                                <div key={entry.id} className="p-4 bg-muted/50 rounded-xl">
                                  <div className="flex items-center justify-between mb-3">
                                    <p className="font-medium">{formatDate(entry.date)}</p>
                                    {entry.mood && (
                                      <div className="flex items-center gap-1">
                                        <MoodIcon className={`w-5 h-5 ${MOOD_ICONS[entry.mood]?.color}`} />
                                        <span className="text-sm text-muted-foreground">{entry.mood_label}</span>
                                      </div>
                                    )}
                                  </div>
                                  {entry.observations && (
                                    <p className="text-sm text-muted-foreground mb-2">{entry.observations}</p>
                                  )}
                                  {entry.symptoms?.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                      {entry.symptoms.map((s) => (
                                        <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                                      ))}
                                    </div>
                                  )}
                                  {entry.medications?.length > 0 && (
                                    <div className="text-xs text-muted-foreground">
                                      Medications: {entry.medications.map(m => m.name).join(", ")}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </ScrollArea>
                      )
                    ) : (
                      <div className="text-center py-12">
                        <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Select a patient to view their journal entries</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="insights">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Symptom Trends */}
                <Card className="glass-card border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Symptom Trends (This Week)</CardTitle>
                    <CardDescription>Most reported symptoms across all patients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {insights?.symptom_trends?.length > 0 ? (
                      <div className="space-y-3">
                        {insights.symptom_trends.map((item, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-sm">{item.symptom}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-primary rounded-full"
                                  style={{ width: `${(item.count / (insights.symptom_trends[0]?.count || 1)) * 100}%` }}
                                />
                              </div>
                              <span className="text-sm text-muted-foreground w-8">{item.count}x</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No symptom data this week</p>
                    )}
                  </CardContent>
                </Card>

                {/* Mood Trends */}
                <Card className="glass-card border-0 shadow-soft">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Mood Trends (This Week)</CardTitle>
                    <CardDescription>Average daily mood across all patients</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {insights?.mood_trends?.length > 0 ? (
                      <div className="flex items-end gap-2 h-32">
                        {insights.mood_trends.map((day, index) => (
                          <div key={index} className="flex-1 flex flex-col items-center gap-2">
                            <div 
                              className="w-full bg-primary/80 rounded-t-lg"
                              style={{ height: `${(day.avg_mood / 5) * 100}%` }}
                              title={`Avg: ${day.avg_mood}`}
                            />
                            <span className="text-xs text-muted-foreground">
                              {new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-muted-foreground py-4">No mood data this week</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
}
